// Method colors

export const METHOD_COLORS = {
  // HTTP verbs
  GET: "#22c984",
  POST: "#4f7ef5",
  PUT: "#f5a623",
  DELETE: "#f05252",
  PATCH: "#a78bfa",

  // Auth
  LOGIN: "#4f7ef5",
  LOGOUT: "#7c8299",
  "LOGIN-CUSTOMER-PORTAL": "#4f7ef5",
  "LOGOUT-CUSTOMER-PORTAL": "#7c8299",
  "REFRESH-CUSTOMER-PORTAL":"#a78bfa",
  "REFRESH": "#a78bfa",

  // Payment / DOKU
  "GENERATE-QRIS-DOKU": "#6f42c1",
  "TOKEN-AUTH-DOKU": "#6610f2",
  "PAYMENT-INQUIRY-DOKU": "#fa840e",
  "PAYMENT-CALLBACK-DOKU": "#e67e22",
  "PAYMENT-CALLBACK-DOKU-REFUND":  "#a2483e",
  "PAYMENT-REFUND-DOKU": "#d65344",
  "PAYMENT-DELETE-DOKU": "#922b21",
  "QRIS-CALLBACK-DOKU": "#8e44ad",
  "PAYMENT-CALLBACK": "#e67e22",

  // VA / invoice
  "VA-GENERATOR": "#1abc9c",
  "VA-Operation": "#16a085",
  "VA-Operation-Void": "#0e6655",
  "Daily-Sync-Invoice-VA": "#1e8449",
  "Send-Invoice-VA": "#27ae60",
  "RENEW-INVOICE-VA": "#2ecc71",
  "Send-PSB-VA": "#58d68d",

  // Messaging / gateway
  "WA-SENDER": "#25d366",
  "GATEWAY-WA-SENDER": "#128c7e",
  "MAIL-SENDER": "#3498db",
  "GATEWAY-MAIL-INIT": "#2980b9",
  "GATEWAY-MAIL-SENDER": "#1a5276",
  "BROADCAST": "#5dade2",
  "RESEND": "#85c1e9",

  // Auto / scheduled
  "AUTO-GENERATE": "#fca559",
  "AUTO-GENERATE-DYNAMIC": "#ca6f1e",
  "AUTO-GENERATE-DYNAMIC-PREPAID": "#a04000",
  "WO-AUTO-SUSPEND": "#d35400",
  "PSB-AUTO-SUSPEND": "#ba4a00",

  // Import / storage
  "IMPORT-CUSTOMER": "#7f8c8d",
  "IMPORT-ITEM": "#95a5a6",
  "storage": "#aab7b8",

  // Fallback handled by getMethodColor()
};

// Status helpers

const NAMED_SUCCESS = new Set(["SUCCESS", "TRUE", "true"]);
const NAMED_FAIL = new Set(["FAIL", "NO-PAYMENT"]);

/**
 * Returns a bucketed class string for a status value.
 * Handles numeric codes AND named strings from the API.
 */
export function getStatusClass(status) {
  const s = String(status ?? "").trim();

  if (NAMED_SUCCESS.has(s) || NAMED_SUCCESS.has(s.toUpperCase())) return "2xx";
  if (NAMED_FAIL.has(s) || NAMED_FAIL.has(s.toUpperCase())) return "5xx";

  const code = parseInt(s, 10);
  if (isNaN(code)) return "2xx";
  if (code >= 500) return "5xx";
  if (code >= 400) return "4xx";
  if (code >= 300) return "3xx";
  return "2xx";
}

/**
 * Human-readable group label for chart axes / legends.
 */
export function getStatusGroup(status) {
  return getStatusClass(status);
}

// Method color lookup

export function getMethodColor(method) {
  return METHOD_COLORS[method] ?? "#7c8299";
}

// Latency extraction

export function extractLatency(response) {
  if (!response) return null;
  if (response.latency  != null) return response.latency;
  if (response.duration != null) return response.duration;
  if (response.elapsed  != null) return response.elapsed;
  return null;
}

// Stats computation

export function computeStats(logs, maxLen) {
  const total = logs.length;
  if (total === 0) {
    return { total: 0, successRate: 0, count2xx: 0, count4xx: 0, count5xx: 0, avgLatency: null };
  }

  let count2xx = 0, count4xx = 0, count5xx = 0;
  let latencySum = 0, latencyCount = 0;

  logs.forEach((l) => {
    const group = getStatusClass(l.status);
    if (group === "5xx") count5xx++;
    else if (group === "4xx") count4xx++;
    else count2xx++;

    const lat = extractLatency(l.response);
    if (lat != null) { latencySum += lat; latencyCount++; }
  });

  return {
    total:       maxLen ?? total,
    successRate: Math.round((count2xx / total) * 100),
    count2xx,
    count4xx,
    count5xx,
    avgLatency:  latencyCount > 0 ? Math.round(latencySum / latencyCount) : null,
  };
}

// Chart data builders

export function buildMethodChartData(logs) {
  const counts = {};
  logs.forEach((l) => {
    counts[l.method] = (counts[l.method] || 0) + 1;
  });
  const labels = Object.keys(counts);
  return {
    labels,
    datasets: [{
      data: labels.map((m) => counts[m]),
      backgroundColor: labels.map((m) => getMethodColor(m)),
      borderWidth: 0,
      hoverOffset: 4,
    }],
  };
}

export function buildStatusChartData(logs) {
  const buckets = { "2xx": 0, "3xx": 0, "4xx": 0, "5xx": 0 };
  logs.forEach((l) => {
    const g = getStatusClass(l.status);
    buckets[g]++;
  });

  // Drop empty buckets so the chart stays clean
  const activeKeys = Object.keys(buckets).filter((k) => buckets[k] > 0);

  const colorMap = {
    "2xx": "#22c984",
    "3xx": "#4f7ef5",
    "4xx": "#f5a623",
    "5xx": "#f05252",
  };

  return {
    labels: activeKeys,
    datasets: [{
      data: activeKeys.map((k) => buckets[k]),
      backgroundColor: activeKeys.map((k) => colorMap[k]),
      borderRadius: 4,
      borderSkipped: false,
    }],
  };
}

// Chart base options

export const chartBaseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => ` ${ctx.label}: ${ctx.raw}`,
      },
    },
  },
};

export const barOptions = {
  ...chartBaseOptions,
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#4a5068", font: { size: 10 } },
    },
    y: {
      grid: { color: "rgba(74,80,104,0.18)" },
      ticks: { color: "#4a5068", font: { size: 10 } },
    },
  },
};

// JSON syntax highlighter

export function highlightJson(str) {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      (match) => {
        if (/^"/.test(match)) {
          if (/:$/.test(match)) return `<span class="dv-json-key">${match}</span>`;
          return `<span class="dv-json-str">${match}</span>`;
        }
        if (/true|false/.test(match)) return `<span class="dv-json-bool">${match}</span>`;
        if (/null/.test(match))       return `<span class="dv-json-null">${match}</span>`;
        return `<span class="dv-json-num">${match}</span>`;
      }
    );
}